import { PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import startCase from 'lodash/startCase';
import { motion, useMotionValue } from 'motion/react';
import { useForm } from 'react-hook-form';

import { useTranslate } from '@i18n';
import { useCategories, useUiNodes, useUiGraph } from '@queries';
import { relationsByParentMapAtom, uiNodesMapAtom } from '@store';
import {
    Box,
    ChartLineIcon,
    CloseIcon,
    Input,
    PlusCircleIcon,
    GridNineIcon,
    Text,
    u,
    Divider,
    colors,
    ToggleButton,
} from '@ui';

import { ModuleSelectionItem } from '../modals/ModuleSelectionModal/ModuleSelectionItem';
import { ModuleList } from '../modals/ModuleSelectionModal/ModuleSelectionModal';
import { Pill, PillGroup } from '../PillGroup/PillGroup';

import {
    selectedAreaAtom,
    gridModulesAtom,
    indicatorColSpanAtom,
    indicatorIndexAtom,
    indicatorRowSpanAtom,
    slotSizeAtom,
    graphLinesMapAtom,
    toggleGraphLineAtom,
} from './atoms';
import * as css from './GridAreaControls.css';
import { LayoutModule, Size, Span } from './types';
import { getMovement, getNextIndex, getOccupiedIndexes, getPositionChange } from './utils';

const nameFromId = (id: string) => startCase(id.split('.')[1] ?? 'Unknown');

type GridAreaIndicatorProps = { onClick: () => void };

const GridAreaSlot = ({ onClick }: GridAreaIndicatorProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const setSlotSize = useSetAtom(slotSizeAtom);

    useEffect(() => {
        const x = ref.current?.offsetWidth;
        const y = ref.current?.offsetHeight;
        if (!x || !y) {
            return;
        }
        setSlotSize((prev) => {
            if (!prev || prev.x !== x || prev.y !== y) {
                return { x, y };
            }
            return prev;
        });
    }, [setSlotSize]);
    return (
        <Box className={css.areaPlaceholder} onClick={onClick} ref={ref}>
            <Box className={css.areaAddIcon}>
                <PlusCircleIcon size={u(6)} weight="fill" />
            </Box>
        </Box>
    );
};

const GraphLineControls = ({ moduleId }: { moduleId: string }) => {
    const relationsByParent = useAtomValue(relationsByParentMapAtom);
    const nodesMap = useAtomValue(uiNodesMapAtom);
    const childrenNodes = useMemo(
        () => relationsByParent[moduleId]?.map((relation) => nodesMap[relation.childUiNodeId]).filter(Boolean),
        [moduleId, nodesMap, relationsByParent],
    );

    const t = useTranslate();

    const graphLinesMap = useAtomValue(graphLinesMapAtom);
    const toggleGraphLine = useSetAtom(toggleGraphLineAtom);
    const enabledGraphLines = graphLinesMap[moduleId];
    // by default, if there are not settings provided to the layout, all graph lines are enabled
    const allEnabled = !enabledGraphLines;

    return (
        <Box display="flex" flexWrap="wrap" gap={u(0.5)}>
            {childrenNodes.map((node) => (
                <ToggleButton
                    isActive={allEnabled || enabledGraphLines.includes(node.id)}
                    onChange={() => toggleGraphLine({ moduleId, graphLineId: node.id })}
                >
                    {t(node.i18n ?? '-')}
                </ToggleButton>
            ))}
        </Box>
    );
};

type GridAreaItemProps = {
    area: LayoutModule;
    onRemove: () => void;
    onReorder: (reorder: LayoutModule) => void;
};

const GridAreaItem = ({ area, onRemove, onReorder }: GridAreaItemProps) => {
    const currentPosition = { x: area.index % 2, y: Math.floor(area.index / 2) };
    const ref = useRef<HTMLDivElement | null>(null);
    const dragOffsetX = useMotionValue(0);
    const dragOffsetY = useMotionValue(0);

    const [indicatorIndex, setIndicatorIndex] = useAtom(indicatorIndexAtom);
    const setIndicatorRowSpan = useSetAtom(indicatorRowSpanAtom);
    const setIndicatorColSpan = useSetAtom(indicatorColSpanAtom);
    const slotSize = useAtomValue(slotSizeAtom);

    const nodesMap = useAtomValue(uiNodesMapAtom);

    const node = nodesMap[area.moduleId];
    const t = useTranslate();
    return (
        <motion.div
            ref={ref}
            // force snapping to the position and bypass animations
            // when area changes
            key={JSON.stringify(area)}
            className={css.areaItem}
            style={{
                gridColumn: `${currentPosition.x + 1} / span ${area.x}`,
                gridRow: `${currentPosition.y + 1} / span ${area.y}`,
                x: dragOffsetX,
                y: dragOffsetY,
            }}
            initial={false}
            layout
            drag
            dragMomentum={false}
            whileHover={{
                z: 10,
            }}
            whileDrag={{
                opacity: 0.5,
            }}
            onDrag={() => {
                if (!slotSize) return;

                const threshold: Size = {
                    x: slotSize.x / 2,
                    y: slotSize.y / 2,
                };
                const lSpanThreshold: Size = {
                    x: threshold.x / 2,
                    y: threshold.y / 2,
                };
                const hSpanThreshold: Size = {
                    x: lSpanThreshold.x * 3,
                    y: lSpanThreshold.y * 3,
                };

                const moved = getMovement(dragOffsetX, dragOffsetY);

                const isBetweenXPositions = moved.abs.x > lSpanThreshold.x && moved.abs.x < hSpanThreshold.x;
                const isBetweenYPositions = moved.abs.y > lSpanThreshold.y && moved.abs.y < hSpanThreshold.y;

                const span: Span = {
                    x: isBetweenXPositions ? 2 : 1,
                    y: isBetweenYPositions ? 2 : 1,
                };

                const positionChange = getPositionChange(moved, threshold);
                const nextPositionIndex = getNextIndex(currentPosition, positionChange, span);

                setIndicatorColSpan(span.x);
                setIndicatorRowSpan(span.y);
                setIndicatorIndex(nextPositionIndex);
            }}
            onDragEnd={() => {
                if (!slotSize) return;

                setIndicatorIndex(null);

                const threshold: Size = {
                    x: slotSize.x / 2,
                    y: slotSize.y / 2,
                };
                const lSpanThreshold: Size = {
                    x: threshold.x / 2,
                    y: threshold.y / 2,
                };
                const hSpanThreshold: Size = {
                    x: lSpanThreshold.x * 3,
                    y: lSpanThreshold.y * 3,
                };

                const moved = getMovement(dragOffsetX, dragOffsetY);

                const isBetweenXPositions = moved.abs.x > lSpanThreshold.x && moved.abs.x < hSpanThreshold.x;
                const isBetweenYPositions = moved.abs.y > lSpanThreshold.y && moved.abs.y < hSpanThreshold.y;

                const span: Span = {
                    x: isBetweenXPositions ? 2 : 1,
                    y: isBetweenYPositions ? 2 : 1,
                };

                const positionChange = getPositionChange(moved, threshold);
                const nextPositionIndex = getNextIndex(currentPosition, positionChange, span);

                onReorder({ ...area, index: nextPositionIndex, x: span.x, y: span.y });

                setIndicatorRowSpan(1);
                setIndicatorColSpan(1);

                dragOffsetX.set(0);
                dragOffsetY.set(0);
            }}
        >
            {indicatorIndex === null && (
                <Box className={css.handleRemove} onClick={onRemove}>
                    <CloseIcon size={u(2)} weight="bold" />
                </Box>
            )}
            <Box display="flex" gap={u(1)}>
                {area.moduleId.startsWith('graph.') ? (
                    <ChartLineIcon size={18} color={colors.bluegray[500]} />
                ) : (
                    <GridNineIcon size={18} color={colors.bluegray[500]} />
                )}
                <Text variant="h3">{node.i18n ? t(node.i18n) : nameFromId(node.id)}</Text>
            </Box>
            {indicatorIndex === null && node.type === 'graph' && <GraphLineControls moduleId={node.id} />}
        </motion.div>
    );
};

const GridSlots = () => {
    const setSelectedArea = useSetAtom(selectedAreaAtom);
    return (
        <Box className={css.areaRoot}>
            {Array.from({ length: 4 }).map((_, i) => (
                <GridAreaSlot
                    key={i}
                    onClick={() =>
                        setSelectedArea({
                            index: i,
                            x: 1,
                            y: 1,
                            area: [i],
                        })
                    }
                />
            ))}
        </Box>
    );
};

const GridIndicator = () => {
    const indicatorIndex = useAtomValue(indicatorIndexAtom);
    const indicatorColumn = indicatorIndex !== null ? indicatorIndex % 2 : null;
    const indciatorRow = indicatorIndex !== null ? Math.floor(indicatorIndex / 2) : null;
    const indicatorRowSpan = useAtomValue(indicatorRowSpanAtom);
    const indicatorColSpan = useAtomValue(indicatorColSpanAtom);

    return (
        <Box className={css.areaCover}>
            {indicatorIndex !== null && (
                <motion.div
                    className={css.areaIndicator}
                    style={{
                        gridColumn: `${indicatorColumn! + 1} / span ${indicatorColSpan}`,
                        gridRow: `${indciatorRow! + 1} / span ${indicatorRowSpan}`,
                    }}
                />
            )}
        </Box>
    );
};

const hasOverlap = (a: number[], b: number[]) => a.some((index) => b.includes(index));

const GridDragAndDrop = () => {
    const [gridModules, setGridModules] = useAtom(gridModulesAtom);

    return (
        <Box className={css.areaCover}>
            {gridModules.map((area, areaIndex) => (
                <GridAreaItem
                    key={area.moduleId}
                    area={area}
                    onRemove={() => setGridModules((prev) => prev.filter((p) => p !== area))}
                    onReorder={(nextPosition) => {
                        setGridModules((prev) => {
                            const nextArea = { ...nextPosition, area: getOccupiedIndexes(nextPosition) };
                            const next = prev.filter((item, idx) => {
                                if (idx === areaIndex) return false;
                                return !hasOverlap(item.area, nextArea.area);
                            });
                            next.splice(areaIndex, 0, nextArea);
                            return next;
                        });
                    }}
                />
            ))}
        </Box>
    );
};

const SWING_FOUNDATIONS_CATEGORY = 'swing_foundations';

export const useFloorModuleSelectionForm = (selectedIds?: string[]) => {
    const t = useTranslate();
    const { register, watch, setValue } = useForm<{ filter: string; categories: string[] }>({
        defaultValues: { filter: '', categories: [] },
    });

    const selectedCategories = watch('categories');
    const filter = watch('filter');

    const { data: categories } = useCategories('graph_type');

    const categoriesMap = useMemo(
        () => Object.fromEntries(categories?.map((category) => [category.id, category] as const) || []),
        [categories],
    );
    const { data: graphs = [] } = useUiGraph();

    const { data: swingFoundations = [] } = useUiNodes('swing_foundations');

    const { data: modules = [] } = useUiNodes('module');

    const newCustomModule = useMemo(
        () => modules.find((module) => !selectedIds?.includes(module.id)),
        [modules, selectedIds],
    );
    const filteredGraphs = useMemo(
        () =>
            graphs.filter(
                ({ id, i18n, categories }) =>
                    t(i18n || '-')
                        .toLowerCase()
                        .includes(filter.toLowerCase()) &&
                    (!selectedCategories.length || selectedCategories.includes(categories.graphType)) &&
                    !selectedIds?.includes(id),
            ),
        [filter, graphs, selectedCategories, selectedIds, t],
    );

    const orderedGraphs = useMemo(
        () =>
            filteredGraphs.sort(
                (a, b) =>
                    categoriesMap[a.categories.graphType]?.displayOrder -
                    categoriesMap[b.categories.graphType]?.displayOrder,
            ),
        [categoriesMap, filteredGraphs],
    );

    const filteredSwingFoundations = useMemo(() => {
        return swingFoundations
            .filter((module) => module.showOn.includes('floor'))
            .filter(
                ({ i18n, id }) =>
                    t(i18n || '-')
                        .toLowerCase()
                        .includes(filter.toLowerCase()) &&
                    (!selectedCategories.length || selectedCategories.includes(SWING_FOUNDATIONS_CATEGORY)) &&
                    !selectedIds?.includes(id),
            );
    }, [filter, selectedCategories, selectedIds, swingFoundations, t]);

    const tableIsSelected = useMemo(() => selectedIds?.includes('module.table'), [selectedIds]);

    const shouldShowTableModule =
        selectedCategories.length === 0 && !tableIsSelected && 'table'.includes(filter.toLowerCase());

    return {
        register,
        orderedGraphs,
        setValue,
        categories,
        selectedCategories,
        categoriesMap,
        swingFoundations: filteredSwingFoundations,
        newCustomModule,
        shouldShowTableModule,
    };
};

const FloorModuleSelection = () => {
    const modules = useAtomValue(gridModulesAtom);
    const {
        register,
        setValue,
        orderedGraphs,
        categories,
        selectedCategories,
        categoriesMap,
        swingFoundations,
        newCustomModule,
        shouldShowTableModule,
    } = useFloorModuleSelectionForm(modules.map(({ moduleId }) => moduleId));

    const setAreas = useSetAtom(gridModulesAtom);
    const [selectedArea, setSelectedArea] = useAtom(selectedAreaAtom);

    const handleModuleSelect = useCallback(
        (moduleId: string, moduleName: string) => {
            if (!selectedArea) {
                throw new Error('No area selected');
            }
            setAreas((prev) => [...prev, { ...selectedArea, moduleId, moduleName }]);
            setSelectedArea(null);
        },
        [selectedArea, setSelectedArea, setAreas],
    );

    const t = useTranslate();
    return (
        <Box display={'block'} position={'relative'}>
            <Box position="sticky" top={0} pb={u(3)} className={css.filterContainer}>
                <PillGroup search={<Input {...register('filter')} placeholder="Search (layout module)" />}>
                    <Pill
                        isSelected={selectedCategories.includes(SWING_FOUNDATIONS_CATEGORY)}
                        onClick={() => {
                            setValue(
                                'categories',
                                selectedCategories.includes(SWING_FOUNDATIONS_CATEGORY)
                                    ? selectedCategories.filter((sid) => sid !== SWING_FOUNDATIONS_CATEGORY)
                                    : [...selectedCategories, SWING_FOUNDATIONS_CATEGORY],
                                { shouldValidate: true },
                            );
                        }}
                        label={'Swing Foundations'}
                    />
                    {categories?.map(({ id, i18n }) => {
                        const isSelected = selectedCategories.includes(id);
                        return (
                            <Pill
                                key={id}
                                isSelected={isSelected}
                                onClick={() => {
                                    setValue(
                                        'categories',
                                        isSelected
                                            ? selectedCategories.filter((sid) => sid !== id)
                                            : [...selectedCategories, id],
                                        { shouldValidate: true },
                                    );
                                }}
                                label={t(i18n ?? '-')}
                            />
                        );
                    })}
                </PillGroup>
            </Box>
            <Box pt={u(3)}>
                <ModuleList>
                    {shouldShowTableModule ? (
                        <ModuleSelectionItem
                            label={'Table Module'}
                            category="Custom"
                            onClick={() => handleModuleSelect?.('module.table', 'Table Module')}
                        />
                    ) : null}
                    {newCustomModule && (
                        <ModuleSelectionItem
                            label={'New Custom Module'}
                            category="Custom"
                            onClick={() => handleModuleSelect?.(newCustomModule?.id, 'Custom Module')}
                        />
                    )}
                    <Divider />
                    {swingFoundations.map(({ id, i18n }) => (
                        <ModuleSelectionItem
                            key={id}
                            label={t(i18n ?? '-')}
                            category="Swing Foundations"
                            onClick={() => handleModuleSelect?.(id, t(i18n ?? '-'))}
                        />
                    ))}
                    {orderedGraphs.map(({ id, i18n, categories }) => (
                        <ModuleSelectionItem
                            key={id}
                            label={t(i18n ?? '-')}
                            category={t(categoriesMap?.[categories.graphType]?.i18n ?? '-')}
                            onClick={() => handleModuleSelect?.(id, t(i18n ?? '-'))}
                        />
                    ))}
                </ModuleList>
            </Box>
        </Box>
    );
};

export const GridAreaControls = () => {
    const addingArea = useAtomValue(selectedAreaAtom);

    if (addingArea) {
        return <FloorModuleSelection />;
    }

    return (
        <Box display="flex" flexDirection="column" gap={u(2)}>
            <Text variant="label">Floor Screen Areas</Text>
            <Box position="relative">
                <GridSlots />
                <GridDragAndDrop />
                <GridIndicator />
            </Box>
        </Box>
    );
};

export const GridAreaScopeProvider = ({ children }: PropsWithChildren) => {
    return (
        <ScopeProvider
            atoms={[
                gridModulesAtom,
                indicatorIndexAtom,
                indicatorRowSpanAtom,
                indicatorColSpanAtom,
                selectedAreaAtom,
                slotSizeAtom,
                graphLinesMapAtom,
            ]}
        >
            {children}
        </ScopeProvider>
    );
};

export const withGridAreaScope = <C extends React.ComponentType<any>>(Component: C) => {
    type Props = React.ComponentProps<C>;

    const Wrapped = (props: Props) => (
        <GridAreaScopeProvider>
            <Component {...props} />
        </GridAreaScopeProvider>
    );

    Wrapped.displayName = `withGridAreaScope(${Component.displayName ?? Component.name ?? 'Component'})`;
    return Wrapped;
};
